import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import { Shield, Folder } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { BadgeSuccess, OptionCardGroup, StepActions, PageHeader } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setIncomeDeclarationOption } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { usePostValidateNextStageMutation } from "@api/applications";
import { ApiError } from "@interfaces/response";
import { PATHS_TOFU_V2, STATUS_BURO_BLOQUEADO, STATUS_CATALOG } from "@helpers/constantes";
import { getConfigName, getIncomeBranchStatusToDiscard, getNextStatus } from "@helpers/funciones";
import { getDecisionIngresoConfig } from "@helpers/decisionIngresoConfig";
import useStatusConfig from "@hooks/useStatusConfig";

type IncomeOption = "sat" | "documents";

const SCREEN_STATUS = STATUS_CATALOG.desicion_sat.code;

/**
 * "¿Cómo quieres declarar tus ingresos?" (status CSDSA). Textos y status de cada vía por empresa
 * (`decisionIngresoConfig`). Con `branches`, al continuar avanza al primer status de la vía elegida y la
 * guarda en `income_declaration_option`, que `useStatusConfig` usa para brincarse la otra vía; como en el
 * legacy (`DesicionCiec`), una vez elegida ya no se puede cambiar.
 */
export const DecisionIngreso = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const configName = getConfigName(window.location.href);
  const config = getDecisionIngresoConfig(configName);
  const { previousStatus } = useStatusConfig(SCREEN_STATUS);

  const statusConfig = useSelector((state: RootState) => state.app.status_config);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const loanStatus = useSelector((state: RootState) => state.register.loan_status);
  const lockedOption = useSelector((state: RootState) => state.register.income_declaration_option);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  // Como en el diseño, la vía recomendada (SAT) viene seleccionada; si ya eligió una, se queda esa.
  const [viaIngreso, setViaIngreso] = useState<IncomeOption>(lockedOption ?? "sat");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onBack = () => navigate(previousStatus?.url || config.backPath || PATHS_TOFU_V2.validacionTelefono.path);

  const onNext = async () => {
    // Empresas sin `branches`: solo navegan, como antes.
    if (!config.branches) {
      navigate((viaIngreso === "documents" ? config.docs.nextPath : config.sat.nextPath) ?? "");
      return;
    }

    try {
      setIsSubmitting(true);
      // Se calcula con la vía recién elegida: `useStatusConfig` todavía no la tiene en el store.
      const discard = [STATUS_BURO_BLOQUEADO, ...getIncomeBranchStatusToDiscard(statusConfig, configName, viaIngreso)];
      const nextStatus = getNextStatus(statusConfig, SCREEN_STATUS, discard);
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status de la vía elegida");
      }

      // Solo la primera vez: si regresó a la decisión, su status ya va más adelante.
      if (loanStatus === SCREEN_STATUS) {
        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();
      }

      dispatch(setIncomeDeclarationOption(viaIngreso));
      navigate(`/${nextStatus.url.replace(/^\//, "")}`);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "DecisionIngreso", values: { viaIngreso } } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader eyebrow="Decisión" title="¿Cómo quieres declarar tus ingresos?" subtitle={config.subtitle} />

      <Box sx={{ mt: "4px" }}>
        <OptionCardGroup
          value={viaIngreso}
          onChange={(v) => setViaIngreso(v as IncomeOption)}
          options={[
            {
              value: "sat",
              icon: <Shield />,
              label: (
                <>
                  {config.sat.label} <BadgeSuccess>Recomendada</BadgeSuccess>
                </>
              ),
              sub: config.sat.sub,
              testId: "decision-ingreso-sat-option",
              disabled: Boolean(lockedOption) && lockedOption !== "sat",
            },
            {
              value: "documents",
              icon: <Folder />,
              label: config.docs.label,
              sub: config.docs.sub,
              testId: "decision-ingreso-docs-option",
              disabled: Boolean(lockedOption) && lockedOption !== "documents",
            },
          ]}
        />
      </Box>

      <StepActions
        onBack={onBack}
        onNext={onNext}
        nextDisabled={isSubmitting}
        backTestId="decision-ingreso-back-button"
        nextTestId="decision-ingreso-continue-button"
      />
    </Box>
  );
};
